/**
 * AppLayout — v.1-D §7.22.
 *
 * The protected chrome shell. Composes Sidebar + Header + ConnectionLossBanner
 * (conditional) + main outlet + NotificationsDrawer + CommandPalette + (DEV)
 * TweaksPanel. The ConnectionLossBanner conditional mount IS the contract —
 * the banner appears ONLY when `useConvexConnectionState() === 'reconnecting'`.
 *
 * TweaksPanel is lazy-imported behind `import.meta.env.DEV` so production
 * bundles tree-shake it entirely (D-028 + the V.24 acceptance check). The
 * dev/spark.ts helper that backs the panel is also tree-shaken in prod.
 *
 * getActiveId(pathname) is a pure helper: URL → Sidebar item id. It is the
 * AppLayout's only routing-state knowledge. The reverse map ID_TO_PATH lets
 * the Sidebar click-handler navigate without round-tripping through React
 * Router state.
 */
import React, { useEffect, useMemo, useState } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { Sidebar, Header, NotificationsDrawer, CommandPalette } from '../design';
import { useNavBadges } from '../hooks/useNavBadges';
import { useConvexConnectionState } from '../hooks/useConvexConnectionState';
import { ConnectionLossBanner } from '../components/shared/ConnectionLossBanner';

// Strict tree-shake gate: the lazy declaration itself is wrapped in a
// `import.meta.env.DEV` ternary so production builds drop both the
// `React.lazy` registration AND the `import('../dev/TweaksPanel')` dynamic
// import. Without this gate, Rollup emits a TweaksPanel chunk into `dist/`
// even though the JSX call site is dead — Vite still walks dynamic imports
// at module-init time. The DEV-true branch is a no-op `() => null` factory
// in prod, so the entire dev surface is grep-clean from `dist/` (V.24).
const TweaksPanelLazy = import.meta.env.DEV
  ? React.lazy(() =>
      import('../dev/TweaksPanel').then((m) => ({ default: m.TweaksPanel })),
    )
  : null;

function getActiveId(pathname: string): string {
  if (pathname === '/') return 'dashboard';
  if (pathname.startsWith('/channels')) return 'channels';
  if (pathname.startsWith('/library')) return 'library';
  if (
    pathname.startsWith('/schedules') ||
    pathname === '/guide' ||
    pathname === '/feeds'
  ) {
    return 'schedules';
  }
  if (pathname.startsWith('/compliance')) return 'compliance';
  if (pathname.startsWith('/batch')) return 'batch';
  if (pathname.startsWith('/editorial')) return 'editorial';
  if (pathname.startsWith('/templates')) return 'templates';
  if (pathname.startsWith('/publishing')) return 'publishing';
  if (pathname.startsWith('/analytics')) return 'analytics';
  if (pathname.startsWith('/quality')) return 'quality';
  if (pathname.startsWith('/cost')) return 'cost';
  if (pathname.startsWith('/uptime')) return 'uptime';
  if (pathname.startsWith('/spend')) return 'spend';
  if (pathname.startsWith('/player-health')) return 'player';
  if (pathname.startsWith('/monitoring')) return 'monitor';
  if (pathname.startsWith('/system-health')) return 'health';
  if (pathname.startsWith('/automations')) return 'automations';
  if (pathname.startsWith('/version-history')) return 'versions';
  if (pathname.startsWith('/alerts')) return 'alerts';
  if (pathname.startsWith('/admin')) return 'admin';
  if (pathname.startsWith('/settings')) return 'settings';
  if (pathname.startsWith('/help')) return 'help';
  return '';
}

const ID_TO_PATH: Record<string, string> = {
  dashboard: '/',
  channels: '/channels',
  library: '/library',
  schedules: '/schedules',
  guide: '/guide',
  compliance: '/compliance',
  batch: '/batch-config',
  editorial: '/editorial',
  templates: '/templates',
  publishing: '/publishing',
  analytics: '/analytics',
  quality: '/quality',
  cost: '/cost',
  uptime: '/uptime',
  spend: '/spend',
  player: '/player-health',
  monitor: '/monitoring',
  health: '/system-health',
  automations: '/automations',
  versions: '/version-history',
  alerts: '/alerts',
  settings: '/settings',
  help: '/help',
};

export function AppLayout(): React.JSX.Element {
  const location = useLocation();
  const navigate = useNavigate();
  const badges = useNavBadges();
  const connection = useConvexConnectionState();
  const [notificationsOpen, setNotificationsOpen] = useState(false);
  const [cmdOpen, setCmdOpen] = useState(false);

  // Build clickable breadcrumb trail. Each non-last segment links to the
  // accumulated path so the operator can hop back up the tree.
  const breadcrumbs = useMemo(() => {
    const segments = location.pathname.split('/').filter(Boolean);
    const out: Array<{ label: string; path?: string }> = [
      { label: 'Dashboard', path: '/' },
    ];
    let acc = '';
    for (let i = 0; i < segments.length; i++) {
      acc += `/${segments[i]}`;
      const label = (segments[i] ?? '').charAt(0).toUpperCase()
        + (segments[i] ?? '').slice(1).replace(/-/g, ' ');
      out.push({ label, path: acc });
    }
    return out;
  }, [location.pathname]);

  // ⌘K (Mac) / Ctrl+K (others) opens the command palette from anywhere.
  //
  // Capture phase + preventDefault + stopPropagation: Ctrl+K in Firefox
  // focuses the address bar search field by default. Listening in the
  // capture phase lets us intercept the event before Firefox's chrome
  // handler sees it. Slack/Linear use the same pattern.
  //
  // Cmd+J / Ctrl+J is offered as a fallback for browsers that still steal
  // Ctrl+K (e.g. some Firefox configurations with sticky bindings).
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      const mod = e.metaKey || e.ctrlKey;
      if (!mod || e.shiftKey || e.altKey) return;
      if (e.key === 'k' || e.key === 'K' || e.key === 'j' || e.key === 'J') {
        e.preventDefault();
        e.stopPropagation();
        setCmdOpen(true);
      }
    };
    document.addEventListener('keydown', onKey, /* useCapture */ true);
    return () => document.removeEventListener('keydown', onKey, true);
  }, []);

  return (
    <div
      style={{
        display: 'flex',
        height: '100vh',
        overflow: 'hidden',
        background: 'var(--snow)',
      }}
    >
      <Sidebar
        active={getActiveId(location.pathname)}
        onNavigate={(id: string) => {
          const path = ID_TO_PATH[id];
          if (path) navigate(path);
        }}
        alertsCount={badges.alertsCount}
        channelsLive={badges.channelsLive}
        pendingAutomations={badges.pendingAutomations}
      />
      <div
        style={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
        }}
      >
        {connection === 'reconnecting' && <ConnectionLossBanner />}
        <Header
          breadcrumbs={breadcrumbs}
          onOpenNotifications={() => setNotificationsOpen(true)}
          onOpenCmd={() => setCmdOpen(true)}
          alertsCount={badges.alertsCount}
        />
        <main style={{ flex: 1, overflowY: 'auto' }}>
          <Outlet />
        </main>
      </div>
      <NotificationsDrawer
        open={notificationsOpen}
        onClose={() => setNotificationsOpen(false)}
      />
      <CommandPalette
        open={cmdOpen}
        onClose={() => setCmdOpen(false)}
        onNavigate={(path: string) => {
          setCmdOpen(false);
          navigate(path);
        }}
      />
      {import.meta.env.DEV && TweaksPanelLazy && (
        <React.Suspense fallback={null}>
          <TweaksPanelLazy />
        </React.Suspense>
      )}
    </div>
  );
}
